import { useState } from 'react'
import { Card, Button, DataTable, EmptyState, LoadingState, ErrorState } from '@/components/ui'
import { useTimeBudgetStatus, useImportTimeBudget } from '../api/timeBudgetApi'
import type { TimeBudgetEmployeeEntry } from '../api/timeBudgetApi'
import type { ColumnDef } from '@tanstack/react-table'
import { Clock, AlertTriangle, TrendingUp, Users } from 'lucide-react'

interface Props {
  contractId: string
  budgetMinutesPerUser: number | null
  overtageHourlyRateCents: number | null
}

function fmtMinutes(min: number): string {
  const h = Math.floor(min / 60)
  const m = min % 60
  if (h === 0) return `${m} Min`
  if (m === 0) return `${h} h`
  return `${h} h ${m} Min`
}

function fmtEur(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100)
}

function AmpelDot({ percent }: { percent: number }) {
  const color = percent >= 100 ? 'bg-red-500' : percent >= 75 ? 'bg-yellow-400' : 'bg-green-500'
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} />
}

function ProgressBar({ percent }: { percent: number }) {
  const clamped = Math.min(percent, 100)
  const color = percent >= 100 ? 'bg-red-500' : percent >= 75 ? 'bg-yellow-400' : 'bg-green-500'
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-200 rounded-full h-1.5">
        <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${clamped}%` }} />
      </div>
      <span className="text-xs text-gray-600 w-10 text-right">{percent} %</span>
    </div>
  )
}

const employeeColumns: ColumnDef<TimeBudgetEmployeeEntry>[] = [
  {
    id: 'status',
    header: '',
    size: 30,
    cell: ({ row }) => <AmpelDot percent={row.original.usedPercent} />,
  },
  {
    accessorKey: 'employeeName',
    header: 'Techniker',
    cell: ({ row }) => row.original.employeeName ?? row.original.employeeTanssId,
  },
  {
    accessorKey: 'budgetMinutes',
    header: 'Budget',
    cell: ({ row }) => fmtMinutes(row.original.budgetMinutes),
  },
  {
    accessorKey: 'totalMinutes',
    header: 'Ist',
    cell: ({ row }) => fmtMinutes(row.original.totalMinutes),
  },
  {
    accessorKey: 'usedPercent',
    header: 'Auslastung',
    cell: ({ row }) => <ProgressBar percent={row.original.usedPercent} />,
  },
]

export function ZeitbudgetSection({ contractId, budgetMinutesPerUser }: Props) {
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth())

  const { data, isLoading, error, refetch } = useTimeBudgetStatus(contractId, year, month)
  const importMutation = useImportTimeBudget(contractId)

  if (budgetMinutesPerUser == null) {
    return (
      <EmptyState
        title="Kein Zeitbudget konfiguriert"
        description="Hinterlegen Sie unter Stammdaten ein Zeitbudget pro Benutzer, um das Tracking zu aktivieren."
        icon={<Clock className="w-10 h-10 text-gray-400" />}
      />
    )
  }

  const monthLabel = new Date(year, month - 1, 1).toLocaleString('de-DE', { month: 'long', year: 'numeric' })

  const handleImport = async () => {
    await importMutation.mutateAsync({ year, month })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <select
            className="border rounded px-2 py-1 text-sm"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {new Date(2000, m - 1, 1).toLocaleString('de-DE', { month: 'long' })}
              </option>
            ))}
          </select>
          <select
            className="border rounded px-2 py-1 text-sm"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {[now.getFullYear() - 1, now.getFullYear()].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleImport}
          disabled={importMutation.isPending}
        >
          {importMutation.isPending ? 'Importiere…' : 'Aus TANSS importieren'}
        </Button>
      </div>

      {isLoading && <LoadingState />}
      {error && (
        <ErrorState
          title="Fehler beim Laden"
          description={String(error)}
          onRetry={() => refetch()}
        />
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card>
              <Card.Body className="flex items-start gap-3 py-3">
                <Users className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Benutzer</p>
                  <p className="text-lg font-semibold">{data.userCount}</p>
                </div>
              </Card.Body>
            </Card>
            <Card>
              <Card.Body className="flex items-start gap-3 py-3">
                <Clock className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Budget gesamt</p>
                  <p className="text-lg font-semibold">{fmtMinutes(data.totalBudgetMinutes)}</p>
                </div>
              </Card.Body>
            </Card>
            <Card>
              <Card.Body className="flex items-start gap-3 py-3">
                <TrendingUp className={`w-5 h-5 mt-0.5 shrink-0 ${data.isOverBudget ? 'text-red-500' : data.isWarning ? 'text-yellow-500' : 'text-green-500'}`} />
                <div>
                  <p className="text-xs text-gray-500">Ist gesamt</p>
                  <p className="text-lg font-semibold">{fmtMinutes(data.totalActualMinutes)}</p>
                  <p className="text-xs text-gray-500">{data.totalUsedPercent} % Auslastung</p>
                </div>
              </Card.Body>
            </Card>
            <Card>
              <Card.Body className="flex items-start gap-3 py-3">
                <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${data.overageMinutes > 0 ? 'text-red-500' : 'text-gray-300'}`} />
                <div>
                  <p className="text-xs text-gray-500">Mehraufwand</p>
                  <p className="text-lg font-semibold">{data.overageMinutes > 0 ? fmtMinutes(data.overageMinutes) : '—'}</p>
                  {data.overageSurchargeCents > 0 && (
                    <p className="text-xs text-red-600">{fmtEur(data.overageSurchargeCents)}</p>
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>

          {data.employees.length === 0 ? (
            <EmptyState
              title={`Keine Zeiten für ${monthLabel}`}
              description='Klicken Sie auf "Aus TANSS importieren" um die Zeiten für diesen Monat zu laden.'
            />
          ) : (
            <Card>
              <Card.Header>
                <h3 className="font-medium text-sm">Auslastung je Techniker — {monthLabel}</h3>
              </Card.Header>
              <Card.Body className="p-0">
                <DataTable
                  columns={employeeColumns}
                  data={data.employees}
                  pagination={{ pageSize: 20 }}
                />
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
